import * as React from "react";
import { router, useForm, Head } from "@inertiajs/react";
import { SettingsPage } from "@/types/generated";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert } from "@/components/ui/alert";
import { PrivacySelect, PrivacyLevel } from "@/components/ui/privacy-select";
import {
  Item,
  ItemContent,
  ItemTitle,
  ItemDescription,
  ItemActions,
} from "@/components/ui/item";
import { CheckCircle2, AlertCircle, XCircle } from "lucide-react";
import AuthLayout from "@/layouts/AuthLayout";
import { InertiaPageComponent } from "@/types";

const SettingsShow: InertiaPageComponent<SettingsPage> = ({
  user,
  iracing_connected,
  iracing_needs_reauthorization,
  garage61_connected,
  garage61_oauth_enabled,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = React.useState("");
  const [deletePassword, setDeletePassword] = React.useState("");
  const [patToken, setPatToken] = React.useState("");
  const [patLoading, setPatLoading] = React.useState(false);

  const { data, setData, put, processing, errors } = useForm({
    user: {
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      profile_privacy: (user.profile_privacy as PrivacyLevel) || "public",
      default_event_privacy:
        (user.default_event_privacy as PrivacyLevel) || "friends_only",
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    put("/settings", {
      preserveScroll: true,
    });
  };

  const handleDeleteAccount = () => {
    if (deleteConfirmText === "DELETE" && deletePassword) {
      router.delete("/settings/account", {
        data: { password: deletePassword },
        onError: () => {
          // Clear password on error
          setDeletePassword("");
        },
      });
    }
  };

  const handleIracingReconnect = () => {
    window.location.href = "/auth/iracing";
  };

  const handleForceSync = () => {
    router.post("/settings/sync_garage61", {}, {
      preserveScroll: true,
    });
  };

  return (
    <>
      <Head title="Settings - Timing Tower" />
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-1">
              Settings
            </h2>
            <p className="text-gray-600 font-medium">
              Manage your account settings and preferences
            </p>
          </div>

          <div className="space-y-6">
            {/* Account Information */}
            <Card className="p-6 border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Account Information
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      type="text"
                      value={data.user.first_name}
                      onChange={(e) =>
                        setData("user", {
                          ...data.user,
                          first_name: e.target.value,
                        })
                      }
                      className="mt-1"
                    />
                    {errors["user.first_name"] && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors["user.first_name"]}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      type="text"
                      value={data.user.last_name}
                      onChange={(e) =>
                        setData("user", {
                          ...data.user,
                          last_name: e.target.value,
                        })
                      }
                      className="mt-1"
                    />
                    {errors["user.last_name"] && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors["user.last_name"]}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={data.user.email}
                    onChange={(e) =>
                      setData("user", { ...data.user, email: e.target.value })
                    }
                    className="mt-1"
                  />
                  {errors["user.email"] && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors["user.email"]}
                    </p>
                  )}
                </div>

                <div className="flex justify-end pt-4">
                  <Button type="submit" disabled={processing}>
                    {processing ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </form>
            </Card>

            {/* Privacy Settings */}
            <Card className="p-6 border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Privacy Settings
              </h3>

              <form onSubmit={handleSubmit} className="space-y-6">
                <PrivacySelect
                  label="Profile Privacy"
                  description="Control who can view your profile and track events"
                  value={data.user.profile_privacy}
                  onChange={(value) =>
                    setData("user", { ...data.user, profile_privacy: value })
                  }
                />

                <PrivacySelect
                  label="Default Event Privacy"
                  description="Default privacy level for new track events and imports"
                  value={data.user.default_event_privacy}
                  onChange={(value) =>
                    setData("user", {
                      ...data.user,
                      default_event_privacy: value,
                    })
                  }
                />

                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <Button type="submit" disabled={processing}>
                    {processing ? "Saving..." : "Save Privacy Settings"}
                  </Button>
                </div>
              </form>
            </Card>

            {/* iRacing Connection */}
            <Card className="p-6 border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                iRacing Connection
              </h3>

              <div className="space-y-4">
                {iracing_connected && !iracing_needs_reauthorization ? (
                  <Item
                    variant="outline"
                    className="border-green-200 bg-green-50"
                  >
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                    <ItemContent>
                      <ItemTitle className="text-green-900">
                        Connected
                      </ItemTitle>
                      <ItemDescription className="text-green-700">
                        Your iRacing account is connected and active
                      </ItemDescription>
                    </ItemContent>
                    <ItemActions>
                      <Button
                        onClick={handleIracingReconnect}
                        variant="outline"
                      >
                        Reconnect
                      </Button>
                    </ItemActions>
                  </Item>
                ) : iracing_needs_reauthorization ? (
                  <Item
                    variant="outline"
                    className="border-yellow-200 bg-yellow-50"
                  >
                    <AlertCircle className="w-10 h-10 text-yellow-600" />
                    <ItemContent>
                      <ItemTitle className="text-yellow-900">
                        Authorisation Expired
                      </ItemTitle>
                      <ItemDescription className="text-yellow-700">
                        Your iRacing connection needs to be reauthorised
                      </ItemDescription>
                    </ItemContent>
                    <ItemActions>
                      <Button onClick={handleIracingReconnect}>
                        Reconnect
                      </Button>
                    </ItemActions>
                  </Item>
                ) : (
                  <Item
                    variant="outline"
                    className="border-gray-200 bg-gray-50"
                  >
                    <XCircle className="w-10 h-10 text-gray-400" />
                    <ItemContent>
                      <ItemTitle className="text-gray-900">
                        Not Connected
                      </ItemTitle>
                      <ItemDescription className="text-gray-700">
                        Connect your iRacing account to import your race data
                        automatically
                      </ItemDescription>
                    </ItemContent>
                    <ItemActions>
                      <Button onClick={handleIracingReconnect}>
                        Connect iRacing
                      </Button>
                    </ItemActions>
                  </Item>
                )}
              </div>
            </Card>

            {/* Garage 61 Connection */}
            <Card className="p-6 border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Garage 61 Connection
              </h3>

              <div className="space-y-4">
                {garage61_connected ? (
                  <Item
                    variant="outline"
                    className="border-green-200 bg-green-50"
                  >
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                    <ItemContent>
                      <ItemTitle className="text-green-900">
                        Connected
                      </ItemTitle>
                      <ItemDescription className="text-green-700">
                        Your Garage 61 account is connected
                      </ItemDescription>
                    </ItemContent>
                    <ItemActions>
                      <Button onClick={handleForceSync} variant="outline" className="bg-white border-green-300 text-green-700 hover:bg-green-50">
                        Force Sync
                      </Button>
                    </ItemActions>
                  </Item>
                ) : (
                  <Item
                    variant="outline"
                    className="border-gray-200 bg-gray-50"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full border border-gray-300">
                      <span className="font-bold text-xs text-gray-600">G61</span>
                    </div>
                    <ItemContent>
                      <ItemTitle className="text-green-900">
                        Not Connected
                      </ItemTitle>
                      <ItemDescription className="text-gray-700">
                        Connect your Garage 61 account to enhance your telemetry data
                      </ItemDescription>
                    </ItemContent>
                    <ItemActions>
                      {garage61_oauth_enabled ? (
                        <Button onClick={() => (window.location.href = "/auth/garage61")}>
                          Connect Garage 61
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Input
                            type="password"
                            placeholder="Paste Personal Access Token"
                            value={patToken}
                            onChange={(e) => setPatToken(e.target.value)}
                            className="w-64"
                          />
                          <Button
                            onClick={() => {
                              setPatLoading(true);
                              router.post("/auth/garage61/pat", { token: patToken }, {
                                onFinish: () => setPatLoading(false),
                                preserveScroll: true
                              });
                            }}
                            disabled={!patToken || patLoading}
                          >
                            {patLoading ? "Saving..." : "Save Token"}
                          </Button>
                        </div>
                      )}
                    </ItemActions>
                  </Item>
                )}
              </div>
            </Card>

            {/* Notification Settings - Placeholder */}
            {/* <Card className="p-6 border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Notification Settings
              </h3>

              <div className="text-center py-8">
                <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  Notification settings coming soon
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  You'll be able to customize your notification preferences here
                </p>
              </div>
            </Card> */}

            {/* Danger Zone */}
            <Card className="p-6 border-red-200 bg-red-50/30">
              <h3 className="text-lg font-semibold text-red-900 mb-4">
                Danger Zone
              </h3>

              {!showDeleteConfirm ? (
                <div className="space-y-4">
                  <p className="text-gray-700">
                    Once you delete your account, there is no going back. Please
                    be certain.
                  </p>
                  <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    variant="outline"
                    className="border-red-600 text-red-700 hover:bg-red-50"
                  >
                    Delete Account
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Alert className="border-red-200 bg-red-50">
                    <p className="font-medium text-red-900">
                      This action cannot be undone
                    </p>
                    <p className="text-sm text-red-700 mt-2">
                      This will permanently delete your account, all your track
                      events, lap data, and comments.
                    </p>
                  </Alert>

                  <div>
                    <Label htmlFor="delete_confirm" className="text-gray-700">
                      Type <span className="font-mono font-bold">DELETE</span>{" "}
                      to confirm
                    </Label>
                    <Input
                      id="delete_confirm"
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      className="mt-1 border-red-300 focus:border-red-500 focus:ring-red-500"
                      placeholder="DELETE"
                    />
                  </div>

                  <div>
                    <Label htmlFor="delete_password" className="text-gray-700">
                      Enter your password to confirm
                    </Label>
                    <Input
                      id="delete_password"
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      className="mt-1 border-red-300 focus:border-red-500 focus:ring-red-500"
                      placeholder="Your password"
                      autoComplete="current-password"
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={handleDeleteAccount}
                      disabled={
                        deleteConfirmText !== "DELETE" || !deletePassword
                      }
                      className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Permanently Delete Account
                    </Button>
                    <Button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText("");
                        setDeletePassword("");
                      }}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>
    </>
  );
};

SettingsShow.layout = (page) => (
  <AuthLayout>{page}</AuthLayout>
);

export default SettingsShow;
